import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { getDetailEvent, postCheckBookedDate } from "../services/apiService";
import WebsiteLayout from "../layouts/WebsiteLayout";
import LoadingSpinner from "../components/common/LoadingSpinner";
import ErrorMessage from "../components/common/ErrorMessage";
import BackNavButton from "../components/common/BackNavButton";
import { isApiSuccess, pickAddress, pickImageUrl, pickTitle, resolvePublicAssetUrl } from "../utils/clubDetailHelpers";
import "./BottleDetailPage.css";
import "./BottleAvailabilityPage.css";

function toYmd(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function toMonthLabel(date) {
  return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

function ymdToTime(ymd) {
  if (!ymd) return 0;
  const [y, m, d] = String(ymd).split("-").map(Number);
  if (!y || !m || !d) return 0;
  return new Date(y, m - 1, d).getTime();
}

function eachDateYmd(startYmd, endYmd) {
  if (!startYmd) return [];
  const list = [];
  const d = new Date(`${startYmd}T00:00:00`);
  const e = new Date(`${endYmd || startYmd}T00:00:00`);
  while (d <= e) {
    list.push(toYmd(d));
    d.setDate(d.getDate() + 1);
  }
  return list;
}

function toShortDateLabel(ymd) {
  if (!ymd) return "";
  const [y, m, d] = String(ymd).split("-").map(Number);
  if (!y || !m || !d) return "";
  return new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function bottleOptionId(item, idx) {
  return String(item?.bottleServiceId || item?._id || item?.id || `${idx}`);
}

function bottleIdentityKey(item, idx = 0) {
  const primaryId = String(item?.bottleServiceId || item?._id || item?.id || "").trim();
  if (primaryId) return `id:${primaryId}`;
  const nameKey = String(pickBottleName(item) || "")
    .trim()
    .toLowerCase();
  if (nameKey) return `name:${nameKey}`;
  return `idx:${idx}`;
}

function toBottleEntries(options) {
  return (options || []).map((item, idx) => ({
    id: bottleOptionId(item, idx),
    item,
  }));
}

function pickBottleName(item) {
  return item?.name || item?.title || item?.bottleName || item?.productName || item?.label || "Bottle";
}

function pickBottleImage(item) {
  const raw = item?.image || item?.imageUrl || item?.photo || item?.thumbnail || item?.icon || item?.picture || "";
  if (typeof raw === "string" && raw.trim()) return resolvePublicAssetUrl(raw.trim());
  return pickImageUrl(item || {});
}

function toFiniteNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "string") {
    const cleaned = value.replace(/[^0-9.-]/g, "");
    if (!cleaned) return null;
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : null;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function formatCurrency(value) {
  const amount = toFiniteNumber(value);
  if (amount === null) return "N/A";
  return `$${amount % 1 === 0 ? amount.toFixed(0) : amount.toFixed(2)}`;
}

function pickBottleOnlyPrice(item) {
  const candidates = [
    item?.bottlePrice,
    item?.price,
    item?.amount,
    item?.cost,
    item?.sellingPrice,
    item?.servicePrice,
    item?.bottle_amount,
  ];
  for (const value of candidates) {
    const n = toFiniteNumber(value);
    if (n !== null) return n;
  }
  return 0;
}

function pickReservationPrice(item, fallbackReservationPrice = 0) {
  const candidates = [
    item?.reservationPrice,
    item?.reservation_price,
    item?.reservationAmount,
    item?.reservation_amount,
    item?.reservationFee,
    item?.bookingPrice,
    fallbackReservationPrice,
  ];
  for (const value of candidates) {
    const n = toFiniteNumber(value);
    if (n !== null) return n;
  }
  return 0;
}

function getDefaultReservationPrice(event) {
  const raw =
    event?.bottleService ||
    event?.bottleServices ||
    event?.bottleMenu ||
    event?.menu ||
    event?.bottles ||
    null;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return 0;
  return (
    toFiniteNumber(
      raw?.reservationPrice ??
        raw?.reservation_price ??
        raw?.reservationAmount ??
        raw?.reservation_amount ??
        raw?.reservationFee
    ) ?? 0
  );
}

function toBottleOptionLabel(item, fallbackReservationPrice = 0, availableQtyOverride = null) {
  const name = pickBottleName(item);
  const totalPrice = pickReservationPrice(item, fallbackReservationPrice) + pickBottleOnlyPrice(item);
  return `${name} (Price: ${formatCurrency(totalPrice)})`;
}

function buildBottleCatalog(event) {
  const raw = event?.bottleService || event?.bottleServices || event?.bottleMenu || event?.menu || event?.bottles || {};
  const toList = (value) => (Array.isArray(value) ? value : []);
  const direct = {
    alcoholic: toList(raw?.alcoholic),
    nonAlcoholic: toList(raw?.nonAlcoholic),
  };
  if (direct.alcoholic.length || direct.nonAlcoholic.length) return direct;
  const list = Array.isArray(raw) ? raw : [];
  const alcoholic = [];
  const nonAlcoholic = [];
  list.forEach((item) => {
    const group = String(item?.category || item?.type || item?.group || item?.section || "").toLowerCase();
    if (group.includes("non")) nonAlcoholic.push(item);
    else alcoholic.push(item);
  });
  return { alcoholic, nonAlcoholic };
}

function createSelectionRow(entries) {
  const first = entries?.[0];
  if (!first) return { selectedId: "", quantity: 1 };
  return { selectedId: first.id, quantity: 1 };
}

function getSelectableOptions(entries, rows, rowIdx) {
  const selectedByOthers = new Set(
    rows
      .map((row, idx) => (idx === rowIdx ? null : String(row?.selectedId || "")))
      .filter(Boolean)
  );
  return entries.filter((entry) => !selectedByOthers.has(entry.id));
}

function getUnusedOptions(entries, rows) {
  const selected = new Set(rows.map((row) => String(row?.selectedId || "")).filter(Boolean));
  return entries.filter((entry) => !selected.has(entry.id));
}

function normalizeBottleErrorMessage(message) {
  const text = String(message || "").trim();
  if (!text) return "";
  if (/^no bottle.*select date/i.test(text)) {
    return "No bottle available. Select date.";
  }
  return text;
}

function sumSelectedQuantity(items) {
  return (items || []).reduce((sum, row) => {
    const qty = Number(row?.qunatity ?? row?.quantity ?? 0);
    if (!Number.isFinite(qty)) return sum;
    return sum + Math.max(0, qty);
  }, 0);
}

const RANGE_LIMIT_MESSAGE =
  "Available quantity is reached. You cannot add more than available quantity. If you want, then select other date.";

function resolveAvailableQuantity(item) {
  if (!item || typeof item !== "object") return 0;
  const toNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const total =
    toNum(item?.totalQuantity) ??
    toNum(item?.quantity) ??
    toNum(item?.stockQuantity) ??
    toNum(item?.totalStock);
  const booked =
    toNum(item?.bookedQuantity) ??
    toNum(item?.bookedQty) ??
    toNum(item?.reservedQuantity) ??
    toNum(item?.soldQuantity) ??
    0;

  if (total != null) return Math.max(0, total - booked);

  const available =
    toNum(item?.availableQuantity) ??
    toNum(item?.availableQty) ??
    toNum(item?.remainingQuantity);
  return Math.max(0, available ?? 0);
}

export default function BottleAvailabilityPage() {
  const { eventId = "" } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const hostFromUrl = (searchParams.get("host") || "").trim();
  const fromEventUrl = searchParams.get("fromEvent") === "1";
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(() => toYmd(new Date()));
  const [selectedEndDate, setSelectedEndDate] = useState(() => toYmd(new Date()));
  const [checking, setChecking] = useState(false);
  const [checkError, setCheckError] = useState("");
  const [availabilityMeta, setAvailabilityMeta] = useState("");
  const [availabilityByDate, setAvailabilityByDate] = useState({});
  const [selectedAlcoholic, setSelectedAlcoholic] = useState([]);
  const [selectedNonAlcoholic, setSelectedNonAlcoholic] = useState([]);
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [isPickingRangeEnd, setIsPickingRangeEnd] = useState(false);

  useEffect(() => {
    if (!eventId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await getDetailEvent(eventId);
        if (cancelled) return;
        if (!isApiSuccess(res?.status) || !res?.data) {
          setError(res?.message || "Bottle detail not found");
          setEvent(null);
          return;
        }
        setEvent(res.data);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Error");
          setEvent(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  const title = pickTitle(event || {});
  const address = pickAddress(event || {});
  const defaultReservationPrice = useMemo(() => getDefaultReservationPrice(event), [event]);
  const bottleCatalog = useMemo(() => buildBottleCatalog(event), [event]);
  const alcoholicEntries = useMemo(() => toBottleEntries(bottleCatalog.alcoholic || []), [bottleCatalog.alcoholic]);
  const nonAlcoholicEntries = useMemo(() => toBottleEntries(bottleCatalog.nonAlcoholic || []), [bottleCatalog.nonAlcoholic]);
  const alcoholicEntryById = useMemo(
    () => new Map(alcoholicEntries.map((entry) => [entry.id, entry.item])),
    [alcoholicEntries]
  );
  const nonAlcoholicEntryById = useMemo(
    () => new Map(nonAlcoholicEntries.map((entry) => [entry.id, entry.item])),
    [nonAlcoholicEntries]
  );
  const todayYmd = toYmd(new Date());
  const calendarCells = useMemo(() => {
    const first = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1);
    const dayOffset = first.getDay();
    const lastDay = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < dayOffset; i += 1) cells.push(null);
    for (let d = 1; d <= lastDay; d += 1) cells.push(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [calendarMonth]);

  useEffect(() => {
    setSelectedAlcoholic(alcoholicEntries.length ? [createSelectionRow(alcoholicEntries)] : []);
    setSelectedNonAlcoholic(nonAlcoholicEntries.length ? [createSelectionRow(nonAlcoholicEntries)] : []);
  }, [alcoholicEntries, nonAlcoholicEntries]);

  const updateRow = (setter, rowIdx, patch) => {
    setCheckError("");
    setter((prev) =>
      prev.map((row, idx) => {
        if (idx !== rowIdx) return row;
        const nextQtyRaw = patch?.quantity;
        const nextQty = nextQtyRaw == null ? row.quantity : Math.max(1, Number(nextQtyRaw) || 1);
        return { ...row, ...patch, quantity: nextQty };
      })
    );
  };

  const changeQuantity = (setter, rowIdx, delta) => {
    setCheckError("");
    setter((prev) =>
      prev.map((row, idx) => {
        if (idx !== rowIdx) return row;
        const parsed = parseInt(String(row?.quantity ?? 1), 10);
        const current = Number.isFinite(parsed) ? Math.max(1, parsed) : 1;
        return { ...row, quantity: Math.max(1, current + delta) };
      })
    );
  };
  const addRow = (setter, entries) => {
    setCheckError("");
    setter((prev) => {
      const available = getUnusedOptions(entries, prev);
      if (!available.length) return prev;
      return [...prev, createSelectionRow(available)];
    });
  };
  const removeRow = (setter, rowIdx) => {
    setCheckError("");
    setter((prev) => (prev.length <= 1 ? prev : prev.filter((_, idx) => idx !== rowIdx)));
  };

  const buildPayloadItems = (rows, entries) => {
    const map = new Map(entries.map((entry) => [entry.id, entry.item]));
    return rows
      .map((row) => {
        const item = map.get(String(row.selectedId || ""));
        if (!item) return null;
        return {
          image: pickBottleImage(item),
          name: pickBottleName(item),
          bottleServiceId: String(item?.bottleServiceId || item?._id || item?.id || ""),
          qunatity: String(Math.max(1, Number(row?.quantity || 1))),
        };
      })
      .filter(Boolean);
  };

  const buildAvailabilityPayload = useCallback(() => {
    if (!selectedDate) {
      return { error: "Please select start date." };
    }
    if (!selectedEndDate) {
      return { error: "Please select end date." };
    }
    const alcoholic = buildPayloadItems(selectedAlcoholic, alcoholicEntries);
    const nonAlcoholic = buildPayloadItems(selectedNonAlcoholic, nonAlcoholicEntries);
    const bottleServiceId = [...alcoholic, ...nonAlcoholic]
      .map((item) => String(item?.bottleServiceId || "").trim())
      .filter(Boolean);
    if (!alcoholic.length && !nonAlcoholic.length) {
      return { error: "Please select at least one bottle." };
    }
    return {
      payload: {
        eventId,
        startDate: selectedDate,
        endDate: selectedEndDate || selectedDate,
        isTable: "no",
        type: "BottleService",
        alcoholic,
        nonAlcoholic,
        bottleServiceId,
      },
    };
  }, [selectedDate, selectedEndDate, selectedAlcoholic, selectedNonAlcoholic, alcoholicEntries, nonAlcoholicEntries, eventId]);

  const runAvailabilityCheck = useCallback(async (showLoading = true, options = {}) => {
    const validateSelection = options.validateSelection !== false;
    const built = buildAvailabilityPayload();
    if (built?.error) {
      setCheckError(built.error);
      setAvailabilityMeta("");
      setAvailabilityByDate({});
      return false;
    }
    if (!built?.payload) return false;
    setCheckError("");
    setAvailabilityMeta("");
    if (showLoading) setChecking(true);
    try {
      const res = await postCheckBookedDate(built.payload);
      const requestedDates = eachDateYmd(
        built?.payload?.startDate,
        built?.payload?.endDate || built?.payload?.startDate
      );
      const noBottleMessage = /^no bottle.*select date/i.test(String(res?.message || "").trim());
      if (!isApiSuccess(res?.status)) {
        setCheckError(res?.message || "Bottle service not available.");
        if (noBottleMessage && requestedDates.length) {
          const unavailableMap = {};
          requestedDates.forEach((ymd) => {
            unavailableMap[ymd] = {
              total: 0,
              alcoholic: 0,
              nonAlcoholic: 0,
              items: [],
              isAvailable: false,
              day: "",
            };
          });
          setAvailabilityByDate(unavailableMap);
        } else {
          setAvailabilityByDate({});
        }
        return false;
      }
      const nextByDate = {};
      const cap = Array.isArray(res?.data)
        ? res.data.reduce((max, row) => {
            const ymd = String(row?.date || "").split("T")[0];
            const items = Array.isArray(row?.items) ? row.items : [];
            const alcoholicItems = Array.isArray(row?.alcoholic) ? row.alcoholic : [];
            const nonAlcoholicItems = Array.isArray(row?.nonAlcoholic) ? row.nonAlcoholic : [];
            const totalCap = items.reduce((sum, item) => sum + resolveAvailableQuantity(item), 0);
            const alcCap = alcoholicItems.reduce((sum, item) => sum + resolveAvailableQuantity(item), 0);
            const nonCap = nonAlcoholicItems.reduce((sum, item) => sum + resolveAvailableQuantity(item), 0);
            if (ymd) {
              nextByDate[ymd] = {
                total: totalCap,
                alcoholic: alcCap,
                nonAlcoholic: nonCap,
                items,
                isAvailable: Boolean(row?.isAvailable),
                day: String(row?.day || ""),
              };
            }
            return Math.max(max, totalCap);
          }, 0)
        : null;
      if ((!Array.isArray(res?.data) || res.data.length === 0) && noBottleMessage && requestedDates.length) {
        requestedDates.forEach((ymd) => {
          nextByDate[ymd] = {
            total: 0,
            alcoholic: 0,
            nonAlcoholic: 0,
            items: [],
            isAvailable: false,
            day: "",
          };
        });
      }
      setAvailabilityByDate(nextByDate);
      if (requestedDates.length) {
        const totals = requestedDates.reduce(
          (acc, ymd) => {
            const row = nextByDate?.[ymd];
            acc.total += Math.max(0, Number(row?.total ?? 0));
            acc.alcoholic += Math.max(0, Number(row?.alcoholic ?? 0));
            acc.nonAlcoholic += Math.max(0, Number(row?.nonAlcoholic ?? 0));
            return acc;
          },
          { total: 0, alcoholic: 0, nonAlcoholic: 0 }
        );
        if (totals.total > 0 || totals.alcoholic > 0 || totals.nonAlcoholic > 0) {
          setAvailabilityMeta(
            `Date Availability: Alc:${totals.alcoholic} | Non-Alc:${totals.nonAlcoholic} | Total:${totals.total}`
          );
        } else {
          setAvailabilityMeta("");
        }
      } else if (cap != null && Number.isFinite(cap) && cap > 0) {
        setAvailabilityMeta(`Date Availability: Avl:${cap}`);
      } else {
        setAvailabilityMeta("");
      }
      const requestedAlcoholicQty = sumSelectedQuantity(built?.payload?.alcoholic || []);
      const requestedNonAlcoholicQty = sumSelectedQuantity(built?.payload?.nonAlcoholic || []);
      const requestedTotalQty = requestedAlcoholicQty + requestedNonAlcoholicQty;
      const hasUnavailableDateInRange = requestedDates.some((ymd) => {
        const row = nextByDate?.[ymd];
        return !row || !row?.isAvailable || Number(row?.total ?? 0) <= 0;
      });
      if (hasUnavailableDateInRange && validateSelection) {
        setCheckError("Selected quantity not available for selected date. Please choose other date.");
        return false;
      }
      const firstDateMetrics = requestedDates.length
        ? {
            total: Math.max(0, Number(nextByDate?.[requestedDates[0]]?.total ?? 0)),
            alcoholic: Math.max(0, Number(nextByDate?.[requestedDates[0]]?.alcoholic ?? 0)),
            nonAlcoholic: Math.max(0, Number(nextByDate?.[requestedDates[0]]?.nonAlcoholic ?? 0)),
          }
        : null;
      const hasMismatchedDateQuantities =
        requestedDates.length > 1 &&
        requestedDates.some((ymd) => {
          const row = nextByDate?.[ymd];
          const total = Math.max(0, Number(row?.total ?? 0));
          const alcoholic = Math.max(0, Number(row?.alcoholic ?? 0));
          const nonAlcoholic = Math.max(0, Number(row?.nonAlcoholic ?? 0));
          return (
            total !== firstDateMetrics?.total ||
            alcoholic !== firstDateMetrics?.alcoholic ||
            nonAlcoholic !== firstDateMetrics?.nonAlcoholic
          );
        });
      if (hasMismatchedDateQuantities && validateSelection) {
        setCheckError("Selected quantity not available for selected date. Please choose other date.");
        return false;
      }
      const rangeTotals = requestedDates.reduce(
        (acc, ymd) => {
          const row = nextByDate?.[ymd];
          acc.total += Math.max(0, Number(row?.total ?? 0));
          acc.alcoholic += Math.max(0, Number(row?.alcoholic ?? 0));
          acc.nonAlcoholic += Math.max(0, Number(row?.nonAlcoholic ?? 0));
          return acc;
        },
        { total: 0, alcoholic: 0, nonAlcoholic: 0 }
      );
      const exceedsRangeTotals =
        requestedTotalQty > rangeTotals.total ||
        requestedAlcoholicQty > rangeTotals.alcoholic ||
        requestedNonAlcoholicQty > rangeTotals.nonAlcoholic;
      if (exceedsRangeTotals && validateSelection) {
        setCheckError("Selected quantity not available for selected date. Please choose other date.");
        return false;
      }
      return true;
    } catch (e) {
      setCheckError(e?.message || "Failed to check availability.");
      setAvailabilityByDate({});
      return false;
    } finally {
      if (showLoading) setChecking(false);
    }
  }, [buildAvailabilityPayload]);

  useEffect(() => {
    if (!eventId || loading || error) return;
    const built = buildAvailabilityPayload();
    if (built?.error) {
      setCheckError(built.error);
      setAvailabilityByDate({});
      return;
    }
    const t = setTimeout(() => {
      runAvailabilityCheck(false, { validateSelection: false });
    }, 250);
    return () => clearTimeout(t);
  }, [eventId, loading, error, buildAvailabilityPayload, runAvailabilityCheck]);

  const selectedRangeDates = useMemo(
    () => eachDateYmd(selectedDate, selectedEndDate || selectedDate),
    [selectedDate, selectedEndDate]
  );
  const rangeGroupCapacity = useMemo(
    () =>
      selectedRangeDates.reduce(
        (acc, ymd) => {
          const row = availabilityByDate?.[ymd];
          acc.alcoholic += Math.max(0, Number(row?.alcoholic ?? 0));
          acc.nonAlcoholic += Math.max(0, Number(row?.nonAlcoholic ?? 0));
          return acc;
        },
        { alcoholic: 0, nonAlcoholic: 0 }
      ),
    [selectedRangeDates, availabilityByDate]
  );
  const selectedAlcoholicQty = useMemo(() => sumSelectedQuantity(selectedAlcoholic), [selectedAlcoholic]);
  const selectedNonAlcoholicQty = useMemo(() => sumSelectedQuantity(selectedNonAlcoholic), [selectedNonAlcoholic]);
  const dateWiseBottleQtyMap = useMemo(() => {
    const next = new Map();
    if (!selectedRangeDates.length) return next;
    selectedRangeDates.forEach((ymd) => {
      const row = availabilityByDate?.[ymd];
      const items = Array.isArray(row?.items) ? row.items : [];
      items.forEach((item, idx) => {
        const identity = bottleIdentityKey(item, idx);
        const qty = Math.max(0, resolveAvailableQuantity(item));
        next.set(identity, Math.max(0, Number(next.get(identity) || 0)) + qty);
      });
    });
    return next;
  }, [selectedRangeDates, availabilityByDate]);
  const hasRangeAvailabilityData = selectedRangeDates.some((ymd) => availabilityByDate?.[ymd] != null);
  const isRangeBottleSoldOut =
    hasRangeAvailabilityData &&
    selectedRangeDates.length > 0 &&
    selectedRangeDates.every((ymd) => Number(availabilityByDate?.[ymd]?.total ?? 0) <= 0);
  const disableBottleSelection =
    checking || isRangeBottleSoldOut || /^no bottle.*select date/i.test(String(checkError || "").trim());

  const getBottleMaxQuantity = useCallback(
    (group, selectedId) => {
      if (hasRangeAvailabilityData) {
        const key = String(selectedId || "");
        const source = group === "alcoholic" ? alcoholicEntryById.get(key) : nonAlcoholicEntryById.get(key);
        const identity = source ? bottleIdentityKey(source) : "";
        const mappedBottleQty = identity ? dateWiseBottleQtyMap.get(identity) : null;
        if (mappedBottleQty != null) return Math.max(0, Number(mappedBottleQty));
        return Math.max(
          0,
          Number(group === "alcoholic" ? rangeGroupCapacity.alcoholic : rangeGroupCapacity.nonAlcoholic)
        );
      }
      const key = String(selectedId || "");
      if (!key) return 0;
      const source = group === "alcoholic" ? alcoholicEntryById.get(key) : nonAlcoholicEntryById.get(key);
      return source ? Math.max(0, resolveAvailableQuantity(source)) : 0;
    },
    [
      hasRangeAvailabilityData,
      dateWiseBottleQtyMap,
      rangeGroupCapacity.alcoholic,
      rangeGroupCapacity.nonAlcoholic,
      alcoholicEntryById,
      nonAlcoholicEntryById,
    ]
  );

  const canIncreaseQuantity = useCallback(
    (group, row) => {
      const bottleMax = getBottleMaxQuantity(group, row?.selectedId);
      const currentQty = Math.max(1, Number(row?.quantity || 1));
      const selectedGroupQty = group === "alcoholic" ? selectedAlcoholicQty : selectedNonAlcoholicQty;
      // Per-row cap: allow reaching bottleMax exactly (e.g. 10 not stuck at 9).
      if (currentQty >= bottleMax) return false;
      // Category cap across all rows uses summed date availability, not this bottle's max.
      if (hasRangeAvailabilityData) {
        const groupCap = group === "alcoholic" ? rangeGroupCapacity.alcoholic : rangeGroupCapacity.nonAlcoholic;
        return selectedGroupQty < groupCap;
      }
      return true;
    },
    [
      getBottleMaxQuantity,
      selectedAlcoholicQty,
      selectedNonAlcoholicQty,
      hasRangeAvailabilityData,
      rangeGroupCapacity.alcoholic,
      rangeGroupCapacity.nonAlcoholic,
    ]
  );

  const showRangeLimitError = useCallback(() => {
    setCheckError(RANGE_LIMIT_MESSAGE);
  }, []);

  const handleCheckBottleAvailability = async () => {
    setChecking(true);
    const ok = await runAvailabilityCheck(true);
    setChecking(false);
    if (!ok) return;
    const built = buildAvailabilityPayload();
    if (!built?.payload) return;
    const query = new URLSearchParams();
    if (hostFromUrl) query.set("host", hostFromUrl);
    if (fromEventUrl) query.set("fromEvent", "1");
    const qs = query.toString();
    navigate(`/bottle/${encodeURIComponent(eventId)}/review${qs ? `?${qs}` : ""}`, {
      state: {
        event,
        bookingInput: {
          eventId,
          hostId: hostFromUrl || event?.hostId?._id || event?.hostId || event?.userId?._id || event?.userId || "",
          selectedDate,
          selectedEndDate: selectedEndDate || selectedDate,
          days: Math.max(1, eachDateYmd(selectedDate, selectedEndDate || selectedDate).length),
          alcoholic: built.payload.alcoholic || [],
          nonAlcoholic: built.payload.nonAlcoholic || [],
          bottleServiceId: built.payload.bottleServiceId || [],
          isTable: "no",
          type: "BottleService",
        },
      },
    });
  };

  if (!eventId) {
    return (
      <WebsiteLayout>
        <ErrorMessage message="Missing bottle id." />
      </WebsiteLayout>
    );
  }
  if (loading) {
    return (
      <WebsiteLayout>
        <LoadingSpinner label="Loading availability..." />
      </WebsiteLayout>
    );
  }
  if (error || !event) {
    return (
      <WebsiteLayout>
        <ErrorMessage message={error || "Not found"} />
      </WebsiteLayout>
    );
  }

  return (
    <WebsiteLayout>
      <div className="bottle-page">
        <div className="bottle-content" style={{ marginTop: 0 }}>
          <div className="bottle-inner">
            <div className="bottle-availability-head">
              <BackNavButton
                to={
                  fromEventUrl
                    ? `/club/event/${encodeURIComponent(eventId)}`
                    : hostFromUrl
                      ? `/bottle/${encodeURIComponent(eventId)}?host=${encodeURIComponent(hostFromUrl)}`
                      : `/bottle/${encodeURIComponent(eventId)}`
                }
                ariaLabel="Back"
              />
              <h1 className="bottle-title bottle-title-sm">Select Date</h1>
            </div>
            <p className="bottle-meta-line">{address ? `📍 ${address}` : title}</p>

            <section className="bottle-section">
              <div className="bottle-calendar">
                <div className="bottle-calendar-top">
                  <strong>{toMonthLabel(calendarMonth)}</strong>
                  <div>
                    <button type="button" onClick={() => setCalendarMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1))}>
                      {"<"}
                    </button>
                    <button type="button" onClick={() => setCalendarMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1))}>
                      {">"}
                    </button>
                  </div>
                </div>
                <div className="bottle-calendar-grid">
                  {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                    <div key={d} className="bottle-weekday">
                      {d}
                    </div>
                  ))}
                  {calendarCells.map((cell, idx) => {
                    if (!cell) return <div key={`empty-${idx}`} className="bottle-day empty" />;
                    const ymd = toYmd(cell);
                    const dayAvailability = availabilityByDate?.[ymd];
                    const isKnownUnavailable =
                      dayAvailability != null &&
                      (!dayAvailability?.isAvailable || Number(dayAvailability?.total ?? 0) <= 0);
                    const disabled = ymd < todayYmd || isKnownUnavailable;
                    const startTs = ymdToTime(selectedDate);
                    const endTs = ymdToTime(selectedEndDate || selectedDate);
                    const currentTs = ymdToTime(ymd);
                    const inRange = currentTs >= Math.min(startTs, endTs) && currentTs <= Math.max(startTs, endTs);
                    const active = selectedDate === ymd || selectedEndDate === ymd;
                    return (
                      <button
                        key={ymd}
                        type="button"
                        disabled={disabled}
                        className={`bottle-day ${active ? "active" : ""} ${inRange ? "in-range" : ""}`}
                        onClick={() => {
                          // First click selects single day; second click extends to range.
                          if (!selectedDate || !selectedEndDate || !isPickingRangeEnd) {
                            setSelectedDate(ymd);
                            setSelectedEndDate(ymd);
                            setIsPickingRangeEnd(true);
                          } else if (ymd >= selectedDate) {
                            setSelectedEndDate(ymd);
                            setIsPickingRangeEnd(false);
                          } else {
                            setSelectedDate(ymd);
                            setSelectedEndDate(ymd);
                            setIsPickingRangeEnd(true);
                          }
                        }}
                      >
                        {cell.getDate()}
                      </button>
                    );
                  })}
                </div>
              </div>
              {availabilityMeta ? <p className="bottle-availability-ok">{availabilityMeta}</p> : null}
              {checkError ? <p className="bottle-availability-error">{normalizeBottleErrorMessage(checkError)}</p> : null}
              {Object.keys(availabilityByDate).length ? (
                <div className="bottle-availability-card">
                  <p className="bottle-availability-title">Date Availability</p>
                  <div className="bottle-availability-grid">
                    {selectedRangeDates.map((ymd) => {
                      const row = availabilityByDate?.[ymd];
                      const total = Number(row?.total ?? 0);
                      const soldOut = total <= 0;
                      return (
                        <div key={ymd} className="bottle-availability-cell">
                          <p className="bottle-availability-date">{toShortDateLabel(ymd)}</p>
                          {row?.day ? <p className="bottle-availability-day">{row.day}</p> : null}
                          {soldOut ? (
                            <span className="bottle-pill-sold">Sold out</span>
                          ) : (
                            <span className="bottle-pill-avl">
                              {`Alc:${Number(row?.alcoholic ?? 0)}`}
                              <br />
                              {`Non-Alc:${Number(row?.nonAlcoholic ?? 0)}`}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </section>

            <section className="bottle-section">
              <h2 className="bottle-section-title">Bottle Service</h2>
              <div className="bottle-group-card">
                <h3 className="bottle-group-title">Alcoholic</h3>
                {selectedAlcoholic.map((row, idx) => {
                  const rowMaxQty = getBottleMaxQuantity("alcoholic", row?.selectedId);
                  return (
                  <div key={`alc-${idx}`} className="bottle-picker-row">
                    <label className="bottle-picker-label">{`Bottle ${idx + 1} (Max: ${rowMaxQty})`}</label>
                    <div className="bottle-picker-control-row">
                      {(() => {
                        const options = getSelectableOptions(alcoholicEntries, selectedAlcoholic, idx);
                        return (
                      <select
                        className="bottle-select"
                        value={row.selectedId}
                        disabled={disableBottleSelection}
                        onChange={(e) => updateRow(setSelectedAlcoholic, idx, { selectedId: e.target.value })}
                      >
                        {options.map((entry) => {
                          return (
                            <option key={entry.id} value={entry.id}>
                              {toBottleOptionLabel(entry.item, defaultReservationPrice)}
                            </option>
                          );
                        })}
                      </select>
                        );
                      })()}
                      <div className="bottle-qty-wrap">
                        <button
                          type="button"
                          disabled={disableBottleSelection}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            changeQuantity(setSelectedAlcoholic, idx, -1);
                          }}
                        >
                          -
                        </button>
                        <span>{Math.max(1, Number(row?.quantity || 1))}</span>
                        <button
                          type="button"
                          disabled={disableBottleSelection}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!canIncreaseQuantity("alcoholic", row)) {
                              showRangeLimitError();
                              return;
                            }
                            changeQuantity(setSelectedAlcoholic, idx, 1);
                          }}
                        >
                          +
                        </button>
                      </div>
                      {idx > 0 ? (
                        <button
                          type="button"
                          className="bottle-remove-btn"
                          disabled={disableBottleSelection}
                          onClick={() => removeRow(setSelectedAlcoholic, idx)}
                        >
                          Remove
                        </button>
                      ) : null}
                    </div>
                  </div>
                )})}
                <button
                  type="button"
                  className="bottle-add-btn"
                  onClick={() => {
                    addRow(setSelectedAlcoholic, alcoholicEntries);
                  }}
                  disabled={disableBottleSelection || !getUnusedOptions(alcoholicEntries, selectedAlcoholic).length}
                  style={{ display: getUnusedOptions(alcoholicEntries, selectedAlcoholic).length ? undefined : "none" }}
                >
                  Add another alcoholic bottle
                </button>
              </div>

              <div className="bottle-group-card">
                <h3 className="bottle-group-title">Non-Alcoholic</h3>
                {selectedNonAlcoholic.map((row, idx) => {
                  const rowMaxQty = getBottleMaxQuantity("nonAlcoholic", row?.selectedId);
                  return (
                  <div key={`non-${idx}`} className="bottle-picker-row">
                    <label className="bottle-picker-label">{`Bottle ${idx + 1} (Max: ${rowMaxQty})`}</label>
                    <div className="bottle-picker-control-row">
                      {(() => {
                        const options = getSelectableOptions(nonAlcoholicEntries, selectedNonAlcoholic, idx);
                        return (
                      <select
                        className="bottle-select"
                        value={row.selectedId}
                        disabled={disableBottleSelection}
                        onChange={(e) => updateRow(setSelectedNonAlcoholic, idx, { selectedId: e.target.value })}
                      >
                        {options.map((entry) => {
                          return (
                            <option key={entry.id} value={entry.id}>
                              {toBottleOptionLabel(entry.item, defaultReservationPrice)}
                            </option>
                          );
                        })}
                      </select>
                        );
                      })()}
                      <div className="bottle-qty-wrap">
                        <button
                          type="button"
                          disabled={disableBottleSelection}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            changeQuantity(setSelectedNonAlcoholic, idx, -1);
                          }}
                        >
                          -
                        </button>
                        <span>{Math.max(1, Number(row?.quantity || 1))}</span>
                        <button
                          type="button"
                          disabled={disableBottleSelection}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!canIncreaseQuantity("nonAlcoholic", row)) {
                              showRangeLimitError();
                              return;
                            }
                            changeQuantity(setSelectedNonAlcoholic, idx, 1);
                          }}
                        >
                          +
                        </button>
                      </div>
                      {idx > 0 ? (
                        <button
                          type="button"
                          className="bottle-remove-btn"
                          disabled={disableBottleSelection}
                          onClick={() => removeRow(setSelectedNonAlcoholic, idx)}
                        >
                          Remove
                        </button>
                      ) : null}
                    </div>
                  </div>
                )})}
                <button
                  type="button"
                  className="bottle-add-btn"
                  onClick={() => {
                    addRow(setSelectedNonAlcoholic, nonAlcoholicEntries);
                  }}
                  disabled={disableBottleSelection || !getUnusedOptions(nonAlcoholicEntries, selectedNonAlcoholic).length}
                  style={{ display: getUnusedOptions(nonAlcoholicEntries, selectedNonAlcoholic).length ? undefined : "none" }}
                >
                  Add another non-alcoholic bottle
                </button>
              </div>
            </section>

            <div className="bottle-bottom-action">
              <button
                type="button"
                className="bottle-next-btn"
                onClick={handleCheckBottleAvailability}
                disabled={disableBottleSelection}
              >
                {checking ? "Checking..." : "Next"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </WebsiteLayout>
  );
}
